/*
 * ProfanityLexicons — per-language rule packs for the ASR-token detector.
 *
 * Shape (one entry per language):
 *   rules[]   { id, tier, accuse: RegExp[] }   matched against a NORMALIZED token
 *   excuses[] RegExp[]                          innocent stems that cancel a
 *                                               SUBSTRING-type accusation
 *   fuzzy[]   { w, tier }                       ASR-distortion recovery targets
 *                                               (RU only — metaphone-equality)
 *
 * Tiers (same policy across languages):
 *   0 = hard profanity (the words a broadcaster MUST bleep)   — always on
 *   1 = strong derivatives + slurs                            — default on
 *   2 = rude but not profanity (ass, jerk, Mist…)             — default off
 *   3 = euphemisms / minced oaths (damn, blin, zut…)          — default off
 *
 * Rules run on tokens normalized by ProfanityDetector.normalizeToken(): folded
 * to lowercase, diacritics stripped (Scheiße→scheisse, putón→puton), Arabic
 * tashkeel/alif folded, Hangul jamo composed, leet undone, runs of 3+ chars
 * collapsed to 2. Therefore ALL patterns here are written in that folded form.
 *
 * SINGLE-TOKEN ONLY. Multi-word insults ("ferme ta gueule", "filho da puta",
 * "كس أمك") cannot be matched here — the ASR word list is scanned token by
 * token. Each language's key token carries the rule instead (gueule, puta, كس).
 */
(function (root) {
    'use strict';

    /* ---------------- RU ---------------------------------------------------- */
    /* Rules are ANCHORED to the token start, and prefixed forms are spelled out.
       This is the same principle config/profanity/ru.php has used server-side
       since 2026-06-01 — and the reason that list never bleeped «расслабляться».
       Substring rules (/бля[дтжб]/ matching anywhere) were flagging 55 ordinary
       words out of the 146 098-word RU dictionary: расслабляться, влюбляться,
       благоговение, квебек, лапидарный, гидроочистка, дрочена…
       Measured 2026-08-09 on prod UGC + the full dictionary + three real ASR
       runs: anchoring keeps recall identical (32/32 tokens, 0 lost words on
       audio) and drops the innocent flags to zero with THREE excuses instead
       of thirty. An exception list is a debt that grows with every new word;
       an anchor is a property of the language.

       The PREFIXES in front of a root are Russian's own, generated below — not
       spelled out word by word. Until 09.10.2026 they were: the list knew
       «заеб» but not «въеб», «спизд» but not «отпизд», «похуй» but not
       «похуист», and no «выбляд» at all; and /^ёб/ could never fire, because
       normalizeToken folds ё to е before any rule runs — so «ёбнулся» and
       «ебнулся» both went out unbleeped. Chat 640: a paying user kept 14 such
       words in her own list (въебали, выблядки, отпизженных, ебнулся…);
       measured on 1 532 628 Russian word forms, the generated prefixes flag
       no more ordinary words than the hand list did (see the lexicon test). */
    // prefixes ending in a vowel join any root directly (заеб, распизд needs no sign)
    var RU_PFX_V = 'вы|до|за|на|недо|о|обо|ото|пере|по|подо|пре|при|про|со|у';
    // ending in a consonant: directly before a consonant root (спизд, отпизд) —
    // before a root in «е» Russian writes ъ (въеб, отъеб), people often ь; WITHOUT
    // the sign it is another word altogether: веб, себя, обед
    var RU_PFX_C = 'в|вз|воз|вс|из|ис|над|об|от|под|раз|рас|с|без|бес';
    function ruRoot(root, extra) {
        return new RegExp('^(?:' + RU_PFX_V + '|' + RU_PFX_C + (extra ? '|' + extra : '') + ')?' + root);
    }
    function ruRootE(root) {
        return new RegExp('^(?:' + RU_PFX_V + '|(?:' + RU_PFX_C + ')[ъь])' + root);
    }
    /* A COMPOUND: any stem + the connecting «о» + (a verb prefix) + the root —
       «мозго-еб», «косо-ебить», «осто-пиздеть», «одно-хуйственно»,
       «хитро-вы-ебанный». An anchored rule cannot see them, and a root allowed
       anywhere bleeps ordinary words (above); the connecting vowel is what a
       compound has and «расслабляться» has not.
       The first harvest of what people censor themselves (14.10.2026, RU, 20
       days): one person's own list alone held 29 such words the dictionary let
       through; «нехуй» came from three people. Measured on 1 532 628 word
       forms: 337 flagged before, 337 after — with two excuses («плохую»,
       «лопоухую») and «еб» not followed by «о»/«р» (двоеборье, троебрачный).
       «бля» gets NO compound rule: it flags the verbs in «-облять»
       (приспособлять, уподоблять — 26 forms). */
    function ruCompound(root) {
        return new RegExp('^[а-я]{2,8}о(?:' + RU_PFX_V + '|' + RU_PFX_C + ')?' + root);
    }
    // …before a root in «е» a consonant prefix needs its sign here too:
    // «низко-с-еб-естоимостный» is «себестоимость», not a compound of «еб»
    function ruCompoundE(root) {
        return new RegExp('^[а-я]{2,8}о(?:' + RU_PFX_V + '|(?:' + RU_PFX_C + ')[ъь])?' + root);
    }
    var RU = {
        rules: [
            { id: 'ru.huy', tier: 0, accuse: [
                // «а» — how the recogniser hears «охуеть»; «ни» — нихуя; «не» — нехуй,
                // нехуя (3 people in the first harvest); «к» — «к хуям» written solid;
                // «оа» — «оахуетельны», two people bleeped it themselves (harvest 27.10)
                ruRoot('ху[йиеяю]', 'а|оа|ни|не|к'), /^хули$/, /^хуле$/,
                ruCompound('ху[йиеяю]'), /^од(?:ин|на|но)ху[йя]/      // «один хуй», solid
            ] },
            { id: 'ru.pizd', tier: 0, accuse: [ruRoot('пи[зс][дж]'), ruRoot('пезд'),      // д→ж: спизженный, отпизженных
                ruCompound('пи[зс][дж]'), /^од(?:ин|на|но)пизд/] },                       // остопиздеть · «одна пизда», solid
            { id: 'ru.eb', tier: 0, accuse: [
                // the bare root: ебать, ебло, ебнуть (and ёбнуть — ё is е here)
                /^[еэ]б[аеиоуыянл]/, /^еб$/, ruRootE('еб'),
                /^долб[ао]еб/, /^зайб[ао]/,
                ruCompoundE('еб(?![ор])')     // мозгоеб, косоебить, остоебенить — not двоеборье, троебрачный
            ] },
            { id: 'ru.blyad', tier: 0, accuse: [ruRoot('бля[дтжб]'), /^бля$/, /^бл[ея]$/, /^бл[ия][дт]/, /^бляц/,
                /^(?:да|ты)бля[дт]/] },       // «да блядь», «ты блять», solid
            { id: 'ru.mudak', tier: 1, accuse: [/^муда[кч]/, /^мудил/, /^мудо[зж]/] },
            { id: 'ru.manda', tier: 1, accuse: [/^манд[аыеу]/, /^мандавошк/] },
            { id: 'ru.gandon', tier: 1, accuse: [/^г[ао]ндон/] },
            { id: 'ru.pidor', tier: 1, accuse: [/^пид[оа]р/, /^пидр(?!егул)/] },
            { id: 'ru.zalupa', tier: 1, accuse: [ruRoot('залуп')] },                      // раззалупаться
            { id: 'ru.droch', tier: 1, accuse: [ruRoot('дроч'), ruCompound('дроч')] },    // суходрочка
            { id: 'ru.shlyukha', tier: 1, accuse: [/^шлюх/, /^шлюш/] },
            { id: 'ru.ublyudok', tier: 1, accuse: [/^ублюд/] },
            { id: 'ru.suka', tier: 2, accuse: [/^сук[аиоуе]$/, /^сукин/, /^суч(к|ар|ий|ья|ье)/] },
            { id: 'ru.zhopa', tier: 2, accuse: [/^жоп[аыоуе]/, /^жопн/] },
            { id: 'ru.kher', tier: 2, accuse: [/^хер$/, /^хер(н|ов|ач|ан)/, /^нахер/, /^похер/] },
            { id: 'ru.govno', tier: 2, accuse: [/^говн/, /^говен/] },
            { id: 'ru.dermo', tier: 2, accuse: [/^дерьм/] },
            { id: 'ru.srat', tier: 2, accuse: [/^ср[аи]ть/, /^сран/, /^насра/, /^обоср/, /^высра/] },
            { id: 'ru.padla', tier: 2, accuse: [/^падл/] },
            { id: 'ru.mraz', tier: 2, accuse: [/^мраз/] },
            { id: 'ru.tvar', tier: 2, accuse: [/^твар(ь|и$|ей|ью|ям|юг)/] },
            { id: 'ru.gnida', tier: 2, accuse: [/^гнид/] },
            { id: 'ru.chmo', tier: 2, accuse: [/^чмо$/, /^чмошн/] },
            { id: 'ru.debil', tier: 2, accuse: [/^дебил/] }
        ],
        excuses: [
            /^психу/, /^распсиху/, /страху/, /^застраху/, /^скипидар/,
            // «худо-» + a root is a compound, not «худой»: «худоебина» stood behind this excuse
            /^худ(?!о(?:еб|ху[йиеяю]|пи[зс][дж]))/, /^хуж/, /^хулиган/, /^хулит/, /^хула$/,
            /плоху/, /оух/,      // the compound rule's two traps: «плохую», «сплохует» · «лопоухую», «длинноухую»
            /^ибо$/, /^ибис/, /^ибиц/, /^ибупроф/,
            /^эбол/, /^эбонит/, /^эбен/, /^зебу$/, /^ебитд/, /^ебе[йя]$/,
            /^блат/, /^блох/, /^мандел/, /^хлеб/, /нахлеб/, /колеб/,
            /^требо/, /потреб/, /употреб/, /истреб/, /стреб/,
            /^руб(ля|лем|лях|лям)/, /^дубля/, /^дубле/,
            /команд/, /мандат/, /мандарин/, /мандол/, /^мандал/,
            /издател/, /^оскорб/, /^ребят/, /^жереб/, /^застеб/, /^стеб/,
            /^себ[еяе]/, /^тебя$/, /^теб[ее]$/,
            /^погреб/, /^серебр/, /^учеб/, /^лечеб/, /^херес/,
            /^сукн/, /^сучок$/, /^сучья$/, /^мандр/, /^веб/,
            // still needed under anchoring: these DO start with a mat stem
            /^эбулио/,            // эбулиоскопия — starts with «эб»
            /^говень/, /^говени/, // говение (church) vs говно
            // дрочена — a pancake dish, genuinely starts with «дроч»; its forms only
            // («дроченье» is not the dish, and went out unbleeped under /^дрочен/)
            /^дроч[её]н(?:а|ы|у|е|ой|ою|ам|ами|ах)?$/
        ],
        fuzzy: [
            { w: 'блядь', tier: 0 }, { w: 'блять', tier: 0 },
            { w: 'пизда', tier: 0 }, { w: 'пиздец', tier: 0 }, { w: 'пиздато', tier: 0 },
            { w: 'заебал', tier: 0 }, { w: 'заебало', tier: 0 }, { w: 'заебись', tier: 0 },
            { w: 'ебать', tier: 0 }, { w: 'ебаный', tier: 0 },
            { w: 'хуйня', tier: 0 }, { w: 'хуево', tier: 0 }, { w: 'охуел', tier: 0 },
            { w: 'охуенно', tier: 0 }, { w: 'нахуй', tier: 0 }, { w: 'нихуя', tier: 0 },
            { w: 'похуй', tier: 0 }, { w: 'хуярит', tier: 0 },
            { w: 'мудак', tier: 1 }, { w: 'мудаки', tier: 1 }, { w: 'пидорас', tier: 1 },
            { w: 'гандон', tier: 1 }
        ]
    };

    /* ---------------- EN ---------------------------------------------------- */
    var EN = {
        rules: [
        { id: 'en.fuck', tier: 0, accuse: [/f+u+c+k/, /^fck/, /^fuk/, /motherf/, /^mofo$/] },
        { id: 'en.shit', tier: 0, accuse: [/sh[i1]t(?!ake)/, /bullsh/] },
        { id: 'en.cunt', tier: 0, accuse: [/^cunt/] },
        { id: 'en.nigger', tier: 1, accuse: [/^n[i1]gg[ea]r?/] },
        { id: 'en.faggot', tier: 1, accuse: [/^fag$/, /^fags$/, /^faggot/] },
        { id: 'en.retard', tier: 1, accuse: [/^retard(s|ed|ing)?$/] },
        { id: 'en.dick', tier: 1, accuse: [/^d[i1]ck(?![ie]ns)/, /^dickhead/] },
        { id: 'en.pussy', tier: 1, accuse: [/^pussy$/, /^pussies$/] },
        { id: 'en.bitch', tier: 1, accuse: [/^b[i1]tch/, /sonofab/] },
        { id: 'en.cock', tier: 1, accuse: [/^cock$/, /^cocks$/, /^cocksuck/] },
        { id: 'en.whore', tier: 1, accuse: [/^whore/, /^slut/] },
        { id: 'en.asshole', tier: 1, accuse: [/^asshole/, /dumbass/, /jackass/] },
        { id: 'en.wanker', tier: 1, accuse: [/^wank/, /^tosser$/] },
        { id: 'en.bastard', tier: 2, accuse: [/^bastard/] },
        { id: 'en.ass', tier: 2, accuse: [/^ass$/, /^asses$/, /^arse/] },
        { id: 'en.piss', tier: 2, accuse: [/^piss(ed|es|ing)?$/] },
        { id: 'en.bollocks', tier: 2, accuse: [/^bollock/, /^bugger/] },
        { id: 'en.damn', tier: 3, accuse: [/^damn/, /^goddamn/, /^hell$/, /^crap/, /^freakin/] }
        ],
        excuses: [
        /^shiitake/, /^scunthorpe/, /^skunthorpe/, /^mishit/,
        /^cockpit/, /^cocktail/, /^peacock/, /^hancock/, /^cockroach/,
        /^classes$/, /^assess/, /^assist/, /^assign/, /^asset/, /^assum/, /^assemb/,
        /^bass/, /^pass/, /^mass/, /^grass/, /^glass/, /^class/, /^dickens/,
        /^analy/, /^wanker(?=y)/, /^titan/, /^bitchen/
        ],
        fuzzy: []
        };

        /* ---------------- ES (España + LatAm) ----------------------------------- */
        /* Accents are folded (cabrón→cabron) but Ñ IS PRESERVED on purpose: folding
        it would turn coño into `cono` = "cone", an ordinary word that appears in
        traffic/ice-cream/geometry speech. Keeping ñ makes the rule exact. */
        var ES = {
        rules: [
        { id: 'es.joder', tier: 0, accuse: [/^jode[rt]/, /^jodi[dm]/, /^jodete$/, /^joda$/] },
        { id: 'es.puta', tier: 0, accuse: [/^put[ao]s?$/, /^putada/, /^putear/, /^puton/, /^hijoputa/, /^hijodeputa/, /^hdp$/] },
        { id: 'es.mierda', tier: 0, accuse: [/^mierd/] },
        { id: 'es.cono', tier: 0, accuse: [/^coño/, /^coñaz/] },
        { id: 'es.gilipollas', tier: 0, accuse: [/^gilipoll/, /^gilipuert/] },
        { id: 'es.chingar', tier: 0, accuse: [/^ching[aoue]/, /^chinga[dr]/, /^chingon/] },
        { id: 'es.cabron', tier: 1, accuse: [/^cabron/, /^cabrona/] },
        { id: 'es.polla', tier: 1, accuse: [/^polla$/, /^pollas$/, /^pollon/, /^chupapoll/] },
        { id: 'es.cojones', tier: 1, accuse: [/^cojon(es|azo|udo)/, /^acojon/] },
        { id: 'es.pendejo', tier: 1, accuse: [/^pendej/] },
        { id: 'es.verga', tier: 1, accuse: [/^verga$/, /^vergaz/, /^vergon$/] },
        { id: 'es.maricon', tier: 1, accuse: [/^maricon/, /^marica$/, /^joto$/, /^tortillera/] },
        { id: 'es.zorra', tier: 1, accuse: [/^zorra$/, /^zorras$/, /^perra$/] },
        { id: 'es.boludo', tier: 1, accuse: [/^boludo/, /^pelotudo/, /^weon$/, /^huevon/, /^culiao/, /^conchetumadre/, /^conchatumadre/] },
        { id: 'es.negrata', tier: 1, accuse: [/^negrata/, /^sudaca/, /^mongolic/, /^subnormal/, /^retrasad/] },
        { id: 'es.culero', tier: 2, accuse: [/^culer/, /^culo$/, /^culos$/] },
        { id: 'es.imbecil', tier: 2, accuse: [/^imbecil/, /^idiota$/, /^estupid/, /^tarado$/] },
        { id: 'es.hostia', tier: 3, accuse: [/^hostia/, /^ostia/, /^carajo$/, /^caramba$/, /^jolin/, /^puñeta/, /^maldito/] }
        ],
        excuses: [
        /^putativ/, /^putrefac/, /^putrid/, /^disputa/, /^computa/, /^reputa/, /^amputa/,
        /^mandarina/, /^cojin/, /^cojines/, /^coj[ea]r/,
        /^culmin/, /^cultur/, /^culpa/, /^calcul/, /^oculta/, /^vincul/, /^curricul/, /^culebra/,
        /^pollo$/, /^pollos$/, /^polluel/, /^ampolla/, /^pollit/, /^polleri/,
        /^zorro$/, /^perro$/, /^perrit/,
        /^chingol/, /^vergel/, /^verguenza/, /^vergonz/, /^divergen/, /^convergen/,
        /^maricel/, /^mariposa/, /^maria$/, /^marian/, /^maricarmen/,
        /^bola/, /^boletin/, /^bolso/, /^bolivia/, /^pichon/, /^machupicchu/,
        /^negro$/, /^negra$/, /^negruzc/, /^mongolia$/, /^retraso/, /^retrasar/, /^tortilla/
        ],
        fuzzy: []
        };

        /* ---------------- PT (BR + PT) ------------------------------------------ */
        var PT = {
        rules: [
        { id: 'pt.caralho', tier: 0, accuse: [/^caralh/, /^caralhao/] },
        { id: 'pt.porra', tier: 0, accuse: [/^porra$/, /^porras$/] },
        { id: 'pt.foder', tier: 0, accuse: [/^fode[rt]/, /^fodi[dt]/, /^fodas?$/, /^fodeu$/, /^foda$/] },
        { id: 'pt.puta', tier: 0, accuse: [/^put[ao]s?$/, /^putaria/, /^putona/] },
        { id: 'pt.merda', tier: 0, accuse: [/^merd/] },
        { id: 'pt.buceta', tier: 0, accuse: [/^bucet/, /^xoxota/] },
        { id: 'pt.viado', tier: 1, accuse: [/^viado$/, /^veado$/, /^bicha$/] },
        { id: 'pt.cu', tier: 1, accuse: [/^cu$/, /^cus$/, /^cuzao/, /^cuzinho/] },
        { id: 'pt.pau', tier: 1, accuse: [/^piroca/, /^rola$/, /^pica$/] },
        { id: 'pt.corno', tier: 2, accuse: [/^corno$/, /^cornos$/, /^otari/] },
        { id: 'pt.bosta', tier: 2, accuse: [/^bosta/, /^cagar$/, /^cagou$/] },
        { id: 'pt.idiota', tier: 2, accuse: [/^idiota$/, /^imbecil/, /^babaca/] },
        { id: 'pt.droga', tier: 3, accuse: [/^droga$/, /^caramba$/, /^raios$/, /^diabo$/] }
        ],
        excuses: [
        /^putativ/, /^disputa/, /^computa/, /^reputa/, /^amputa/, /^deputa/,
        /^cuidad/, /^cultur/, /^curso/, /^curs/, /^curi/, /^cume/, /^cumpr/, /^custa/, /^cubo/,
        /^curt/, /^cup[ao]/, /^cura/, /^curr/,
        /^picada/, /^picole/, /^piqueni/, /^pican/,
        /^rolar?$/, /^rolha/, /^rolet/,
        /^manga$/, /^caneta/, /^porta/, /^porto/, /^porcao/, /^porcel/,
        /^merdi/, /^bichano/, /^cornij/, /^cornet/, /^corneta/
        ],
        fuzzy: []
        };

        /* ---------------- DE ---------------------------------------------------- */
        /* ß→ss and umlauts folded: Scheiße→scheisse, Fötze→fotze. */
        var DE = {
        rules: [
        { id: 'de.ficken', tier: 0, accuse: [/^fick/, /^gefickt/, /^verfickt/] },
        // `schiss` (not `schiess`) — beschissen/verschissen fold to -schiss-,
        // while beschießen ("to shoot at") folds to -schiess- and stays clean
        { id: 'de.scheisse', tier: 0, accuse: [/^scheiss/, /^scheise/, /schiss(en|e)/, /^kacke$/] },
        { id: 'de.fotze', tier: 0, accuse: [/^fotze/, /^votze/] },
        { id: 'de.wichser', tier: 0, accuse: [/^wichs/, /^wixx/] },
        { id: 'de.arschloch', tier: 1, accuse: [/^arschloch/, /^arschlocher/] },
        { id: 'de.hurensohn', tier: 1, accuse: [/^hurensohn/, /^hure$/, /^huren$/, /^nutte/] },
        { id: 'de.schwanz', tier: 1, accuse: [/^schwanzlutsch/, /^schwuchtel/] },
        { id: 'de.arsch', tier: 2, accuse: [/^arsch$/, /^arsche$/, /^arschig/] },
        { id: 'de.pisse', tier: 2, accuse: [/^pisse/, /^pissen/, /^angepisst/] },
        { id: 'de.idiot', tier: 2, accuse: [/^idiot/, /^depp$/, /^trottel/, /^blodmann/] },
        { id: 'de.mist', tier: 3, accuse: [/^mist$/, /^verdammt/, /^verflucht/, /^herrgott$/] }
        ],
        excuses: [
        /^sachbearbeit/, /^fichte/, /^fiktion/, /^fixier/, /^figur/,
        /^schifffahrt/, /^schiff/, /^kacheln?$/,
        /^arschitekt/, /^mistel/, /^ministe/, /^mischung/, /^mission/, /^mistral/,
        /^hurtig/, /^hurra/, /^nuttig(?=keit)/, /^pissoir/, /^scheiden/, /^scheitel/, /^scheitern/,
        /^wichtig/, /^wichte/, /^wicht$/, /^depression/, /^deponie/
        ],
        fuzzy: []
        };

        /* ---------------- FR ---------------------------------------------------- */
        var FR = {
        rules: [
        { id: 'fr.putain', tier: 0, accuse: [/^putain/, /^putaing/, /^pute$/, /^putes$/] },
        { id: 'fr.merde', tier: 0, accuse: [/^merde/, /^merdique/, /^emmerd/] },
        { id: 'fr.enculer', tier: 0, accuse: [/^encul/, /^nique$/, /^niquer/, /^niquez/] },
        { id: 'fr.connard', tier: 0, accuse: [/^connard/, /^connasse/, /^conne$/] },
        { id: 'fr.bite', tier: 1, accuse: [/^bite$/, /^bites$/, /^couille/, /^chatte$/] },
        { id: 'fr.salope', tier: 1, accuse: [/^salope(?!tte)/, /^salopes$/, /^salopard/, /^salaud/, /^pouffiasse/] },
        { id: 'fr.foutre', tier: 1, accuse: [/^foutre$/, /^fout(re|ez|ent)?$/, /^foutu/, /^jmenfous$/] },
        { id: 'fr.pedale', tier: 1, accuse: [/^pedale$/, /^tapette$/, /^tarlouze/] },
        { id: 'fr.gueule', tier: 2, accuse: [/^gueule$/, /^gueuler/, /^engueul/] },
        { id: 'fr.con', tier: 2, accuse: [/^con$/, /^cons$/, /^conneri/] },
        { id: 'fr.chier', tier: 2, accuse: [/^chier$/, /^chiant/, /^chie$/] },
        { id: 'fr.zut', tier: 3, accuse: [/^zut$/, /^mince$/, /^punaise$/, /^purée$/, /^puree$/] }
        ],
        excuses: [
        /^depute/, /^deputes/, /^reputa/, /^computa/, /^amputa/, /^dispute/, /^putois/,
        /^conseil/, /^concert/, /^concours/, /^condu/, /^confi/, /^congre/, /^connai/,
        /^connect/, /^connot/, /^conqu/, /^conserv/, /^consid/, /^consom/, /^constit/,
        /^constru/, /^consult/, /^contact/, /^conten/, /^context/, /^continu/, /^contr/,
        /^conven/, /^convers/, /^convict/, /^convo/, /^conom/, /^concl/, /^concep/,
        /^bitume/, /^orbite/, /^cubit/, /^habit/, /^arbitr/,
        /^chiffre/, /^chien/, /^chic$/, /^chiffon/, /^chimie/,
        /^foutbal/, /^salopette/, /^gueuloir/, /^merdier(?=s)/
        ],
        fuzzy: []
        };

        /* ---------------- ID ---------------------------------------------------- */
        /* CRITICAL (mirrors config/profanity/id.php): bare `anjing` = the animal
        "dog", `babi` = "pig", `setan` = "devil", `tai` = also "faeces" in
        clinical speech. They only become insults when DIRECTED ("anjing lo"),
        which is two ASR tokens — unmatchable here. So the bare animals sit in
        tier 2 (default OFF) while the minced interjections that are NEVER the
        animal (anjir/anjay/njir) sit in tier 1. Measured FP this prevents:
        "Anjing tetangga saya sangat lucu" (my neighbour's dog is cute). */
        var ID = {
        rules: [
        { id: 'id.kontol', tier: 0, accuse: [/^kontol/, /^kntl$/, /^titit$/] },
        { id: 'id.memek', tier: 0, accuse: [/^memek$/, /^pepek/, /^pukimak/, /^kimak$/, /^jembut/] },
        { id: 'id.ngentot', tier: 0, accuse: [/^ngentot/, /^ngntot/, /^entot/, /^ngewe$/] },
        { id: 'id.bangsat', tier: 0, accuse: [/^bangsat/] },
        { id: 'id.jancok', tier: 0, accuse: [/^jancok/, /^jancuk/, /^dancok/, /^cukimai/] },
        { id: 'id.bajingan', tier: 1, accuse: [/^bajingan/, /^keparat/, /^kampang$/] },
        { id: 'id.anjir', tier: 1, accuse: [/^anjir$/, /^anjay$/, /^anjrit$/, /^njir$/, /^njing$/] },
        { id: 'id.pelacur', tier: 1, accuse: [/^pelacur/, /^lonte$/, /^jablay$/, /^sundal/, /^banci$/, /^bencong/] },
        { id: 'id.cebong', tier: 1, accuse: [/^cebong/, /^kampret$/, /^kadrun/, /^cokin/, /^kafir$/] },
        { id: 'id.asu', tier: 2, accuse: [/^asu$/] },
        { id: 'id.anjing', tier: 2, accuse: [/^anjing$/] },   // literal dog — user opts in
        { id: 'id.babi', tier: 2, accuse: [/^babi$/] },       // literal pig
        { id: 'id.tai', tier: 2, accuse: [/^tai$/, /^taik$/, /^tahi$/, /^berak$/] },
        { id: 'id.goblok', tier: 2, accuse: [/^goblok/, /^tolol$/, /^bego$/, /^begok$/, /^dungu$/, /^bebal$/] },
        { id: 'id.brengsek', tier: 2, accuse: [/^brengsek/, /^sialan/] },
        { id: 'id.sial', tier: 3, accuse: [/^sial$/, /^astaga$/, /^buset$/, /^setan$/] }
        ],
        excuses: [
        /^taiwan/, /^taipan/, /^taifun/, /^taip$/, /^taiko/, /^pantai/, /^santai/, /^petai/,
        /^rantai/, /^untai/, /^capai/, /^mencapai/, /^setangkai/,
        /^asuh/, /^asuransi/, /^asumsi/, /^asupan/, /^asut/, /^kasur/, /^masuk/, /^pasukan/, /^bahasa/,
        /^kontrak/, /^kontak/, /^kontrol/, /^konten/, /^kontribu/, /^kontan/,
        /^memerlukan/, /^memekik/, /^memekar/, /^pepaya/,
        /^cokelat/, /^coklat/, /^cocok/, /^cukup/, /^cukur/, /^cuka$/,
        /^setangg/, /^sosial/, /^spesial/, /^komersial/, /^material/, /^potensial/, /^inisial/
        ],
        fuzzy: []
        };

        /* ---------------- KO ----------------------------------------------------- */
        /* Whisper emits eojeol tokens ("씨발," / "개새끼가"), so rules are prefix-style.
        CRITICAL (mirrors config/profanity/ko.php): bare 자지 / 보지 are NOT
        profanity — they collide with the verb stems 자다/보다 + the negative
        ending -지 ("자지 마" = don't sleep, "보지 마" = don't look). Only the
        vulgar compounds flag. Same for 시발 (始發 "first departure": 시발점/시발역)
        and 새끼 (animal offspring: 강아지새끼; 새끼손가락 = pinky finger). */
        var KO = {
        rules: [
        { id: 'ko.sibal', tier: 0, accuse: [/^씨발/, /^씨팔/, /^시팔/, /^씨바/, /^쓰발/, /^싸발/, /^쒸발/, /^씨불/, /^ㅅㅂ$/, /^ㅆㅂ$/,
        /^시발(?!점|역|자|차)/] },
        { id: 'ko.jot', tier: 0, accuse: [/^좆/, /^좃/, /^존나/, /^존내/, /^졸라(?!면)/, /^ㅈㄴ$/] },
        { id: 'ko.ssip', tier: 0, accuse: [/^씹(?!니|시|디)/, /^씹할/, /^씹새/] },
        { id: 'ko.gaesaekki', tier: 0, accuse: [/^개새끼/, /^개색기/, /^개세끼/, /^개쉐끼/, /^개쉑/, /^호로새끼/, /^후레새끼/] },
        { id: 'ko.byungsin', tier: 1, accuse: [/^병신/, /^븅신/, /^빙신/, /^병쉰/, /^ㅂㅅ$/, /^ㅄ$/] },
        { id: 'ko.jiral', tier: 1, accuse: [/^지랄/, /^쥐랄/, /^지럴/, /^ㅈㄹ$/] },
        { id: 'ko.saekki', tier: 1, accuse: [/^새끼(?!손|발|줄)/, /^쌔끼/, /^쉐끼/] },
        { id: 'ko.slur', tier: 1, accuse: [/^짱깨/, /^짱께/, /^쪽발이/, /^쪽바리/, /^깜둥이/, /^껌둥이/, /^조센징/, /^니그로/, /^똥남아/] },
        { id: 'ko.vulgar_body', tier: 1, accuse: [/^자지(?!도|만|않|마|말|는|를$)/, /^보지(?!도|만|않|못|마|말|는)/, /^개자지/, /^개보지/] },
        { id: 'ko.michin', tier: 2, accuse: [/^미친(?!듯|척)/, /^또라이/, /^돌아이/, /^미친놈/, /^미친년/] },
        { id: 'ko.gaeXX', tier: 2, accuse: [/^개같/, /^개놈/, /^개년/, /^개소리/, /^개수작/] },
        { id: 'ko.dakchyeo', tier: 2, accuse: [/^닥쳐/, /^닥치/, /^꺼져/, /^꺼지라/, /^엿먹/, /^엿같/] },
        { id: 'ko.babo', tier: 3, accuse: [/^바보/, /^멍청/, /^머저리/, /^찌질/, /^빡쳐/, /^빡침/] },
        { id: 'ko.jenjang', tier: 3, accuse: [/^젠장/, /^제기랄/, /^우라질/, /^빌어먹을/, /^아이씨$/, /^염병/] }
        ],
        excuses: [
        /^시발점/, /^시발역/, /^시발자/, /^시발차/,
        /^보지못/, /^보지도/, /^보지만/, /^보지말/, /^보지마/, /^보지는/,
        /^자지도/, /^자지만/, /^자지않/, /^자지말/, /^자지마/,
        /^새끼손/, /^새끼발/, /^새끼줄/,
        /^강아지새끼/, /^고양이새끼/, /^돼지새끼/,
        /^개나리/, /^개미/, /^개발/, /^개선/, /^개인/, /^개최/, /^개방/, /^개점/, /^개학/, /^개설/, /^개혁/,
        /^미친듯/, /^미친척/,
        /^씨앗/, /^씨름/, /^아저씨/, /^아가씨/,
        /^존경/, /^존재/, /^존중/, /^존댓/, /^존속/,
        /^장애물/, /^장애인/, /^정신장애/, /^발달장애/
        ],
        fuzzy: []
        };

        /* ---------------- AR ------------------------------------------------------ */
        /* Tokens arrive with alif/hamza folded and tashkeel stripped. Definite
        article "ال" is often glued by the ASR — patterns allow it. */
        /* Proclitics the ASR glues onto the stem: و/ف + ب/ك/ل + ال (الشرموطة، وكسمك).
        Rules therefore allow an optional prefix rather than anchoring hard at ^.
        عبد ("slave") is deliberately absent — a lone token cannot be told apart
        from the names عبدالله / عبده or from عبادة "worship". */
        var AR = {
        rules: [
        { id: 'ar.kos', tier: 0, accuse: [/^(و|ف)?(ب|ك|ل)?(ال)?كس$/, /كسم/, /^(و|ف)?(ب|ك|ل)?(ال)?كوس$/, /كسخت/] },
        { id: 'ar.sharmouta', tier: 0, accuse: [/شرموط/, /شراميط/, /قحبه?$/, /قحاب/, /عاهره?$/] },
        { id: 'ar.neek', tier: 0, accuse: [/^(و|ف)?(ب|ك|ل)?نيك/, /منيوك/, /متناك/, /ينيك/, /بينيك/] },
        { id: 'ar.air', tier: 0, accuse: [/^(و|ف)?(ب|ك|ل)?(ال)?اير$/, /^(و|ف)?(ب|ك|ل)?(ال)?زب$/, /^زبي$/, /^(ال)?زبر$/] },
        { id: 'ar.teez', tier: 1, accuse: [/^(و|ف)?(ب|ك|ل)?(ال)?طيز/, /^(ال)?تيز$/] },
        { id: 'ar.ars', tier: 1, accuse: [/^(و|ف)?(ب|ك|ل)?(ال)?عرص/, /معرص/] },
        { id: 'ar.khawal', tier: 1, accuse: [/^(و|ف)?(ب|ك|ل)?(ال)?خول$/, /^(ال)?خولات$/, /^(ال)?لوطي$/, /^(ال)?مخنث/, /^(ال)?شاذ$/, /^(ال)?زنجي$/] },
        { id: 'ar.zbala', tier: 2, accuse: [/^(و|ف)?(ب|ك|ل)?(ال)?زباله?$/, /^(ال)?قذر/, /^(ال)?وسخ/, /^(ال)?خرا$/, /خريان/] },
        { id: 'ar.kalb', tier: 2, accuse: [/^(و|ف)?(ب|ك|ل)?(ال)?كلب$/, /^(ال)?كلاب$/, /^(ال)?حمار$/, /^(ال)?حماره$/] },
        { id: 'ar.ghabi', tier: 2, accuse: [/^(ال)?غبي/, /^(ال)?احمق/, /^(ال)?مغفل/, /^(ال)?متخلف$/, /^(ال)?تافه$/] },
        { id: 'ar.laan', tier: 3, accuse: [/^(ال)?لعنه?$/, /^تبا$/, /يلعن/] }
        ],
        excuses: [
        /^(ال)?كسر/, /^(ال)?كسل/, /^(ال)?كسب/, /^(ال)?كسو/, /^(ال)?كسا/, /مكسور/, /انكسر/, /^(ال)?كسوف/, /^(ال)?كساد/,
        /^باكستان/, /^المكسيك/, /^تكساس/, /^كسرى/, /^اكسجين/, /^اكسيد/, /^اكسسوار/, /^كوسا/, /^كوسه/,
        /^نيكوتين/, /^نيكل/, /^نيكولا/, /^نيكاراغوا/, /^تكنيك/, /^تكنولوجيا/, /^بيكنيك/, /^مانيكير/, /^كلينيك/, /^بنك/, /^بنوك/,
        /^(ال)?زبون/, /^(ال)?زبده/, /^(ال)?زبيب/, /^زبرجد/, /^(ال)?زبور/,
        /^(ال)?كلمه/, /^(ال)?كلام/, /^(ال)?كليه/, /^(ال)?كلف/,
        /^(ال)?حماس/, /^(ال)?حمام/, /^(ال)?حمل/, /^(ال)?حماي/, /^(ال)?حمراء/, /^احمر/,
        /^(ال)?طيب/, /^(ال)?طير/, /^(ال)?طيران/,
        /^(ال)?خراب/, /^(ال)?خرافه?/, /^(ال)?خرج/, /^مخرج/, /^(ال)?خروج/, /^(ال)?خروف/, /^(ال)?خريطه/, /^(ال)?خرطوم/,
        /^(ال)?غبار/, /^(ال)?غياب/, /^(ال)?غباء/, /^(ال)?لوط$/, /^(ال)?بلوط/,
        /^(ال)?عرض/, /^(ال)?عروض/, /^معرض/, /^(ال)?عريض/, /^زنجبيل/,
        /^(ال)?تفاح/, /^(ال)?تفاعل/, /^(ال)?تفاصيل/, /^(ال)?تفاهم/
        ],
        fuzzy: []
        };

        var UK = {
        rules: [
        { id: 'uk.huy', tier: 0, accuse: [/^ху[йяюєї]/, /^хуїв/, /^хуйн/] },
        { id: 'uk.pizd', tier: 0, accuse: [/^пізд/, /^піздець/, /^піздат/] },
        { id: 'uk.yeb', tier: 0, accuse: [/^[єе]б[аеуои]/, /^йоб/, /^виїб/, /^заїб/] },
        { id: 'uk.blyad', tier: 0, accuse: [/^бляд/, /^блядь$/] },
        { id: 'uk.pidar', tier: 0, accuse: [/^під[оа]р/, /^підарас/] },
        { id: 'uk.suka', tier: 1, accuse: [/^сук[аиоу]$/, /^суки$/, /^сучар/] },
        { id: 'uk.mudak', tier: 1, accuse: [/^мудак/, /^мудил/, /^мудо/] },
        { id: 'uk.gandon', tier: 1, accuse: [/^гандон/, /^гондон/] },
        { id: 'uk.kurva', tier: 1, accuse: [/^курв[аиоуі]/, /^шльондр/] },
        { id: 'uk.dovbojob', tier: 1, accuse: [/^довбо[йі]?об/, /^довбень$/] },
        { id: 'uk.slur', tier: 0, accuse: [/^кацап/, /^москал[ья]/, /^хохл[ауиоеі]/, /^жид[иоа]/, /^чурк[аиоуі]/] },
        { id: 'uk.durak', tier: 2, accuse: [/^дурн[ийоа]/, /^ідіот/, /^кретин/, /^бовдур/] },
        { id: 'uk.laino', tier: 2, accuse: [/^лайно/, /^гівн/, /^дупа$/] },
        { id: 'uk.chort', tier: 3, accuse: [/^дідьк/, /^чорт/, /^біс[аиу]$/] }
        ],
        excuses: [
        // Приличные слова, которые начинаются как ругательные корни. Без них
        // цензор ловил бы «сукня» (платье) и «підручник» (учебник).
        /^сукн/, /^сукуп/, /^сукц/, /^суків/,
        /^підруч/, /^підряд/, /^підрах/, /^підроб/, /^підпис/, /^підбір/, /^підказ/,
        /^хуст/, /^худ/, /^хутр/, /^хурм/,
        /^дупл/, /^дублік/, /^дубл/,
        /^бісер/, /^бісквіт/, /^бісект/,
        /^чортополох/,
        /^мудр/, /^мудість/,
        /^блякл/, /^блям/,
        /^курс/, /^курор/, /^курат/, /^куріп/, /^курк/, /^курч/, /^куряч/
        ]
        };

        /* ---------------- TR ---------------------------------------------------- */
        /* normalizeToken() folds ç/ö/ü to c/o/u (they're in the ASCII diacritics
           table) but does NOT touch ş/ğ/ı — those aren't in that table, so they
           survive as their own Unicode letters. Patterns below are written for
           THAT mixed result: göt → got, üç → uc, but şerefsiz/gerizekalı/amına
           keep their ş/ğ/ı as-is. This also means "amın" (vulgar, dotless ı)
           and "amin" (amen, dotted i) never collide — normalizeToken keeps the
           two letters distinct, so no excuse is needed for the religious word. */
        var TR = {
        rules: [
        { id: 'tr.sik', tier: 0, accuse: [/^sik(?!ke|orsk)/, /^siktir/] },
        { id: 'tr.amcik', tier: 0, accuse: [/^amcık/, /^amcığ/, /^amın/, /^amına/] },
        { id: 'tr.yarak', tier: 0, accuse: [/^yarrak/, /^yarrağ/, /^yarak$/, /^yarağ/] },
        { id: 'tr.orospu', tier: 0, accuse: [/^orospu/, /^orspu/] },
        { id: 'tr.gotveren', tier: 1, accuse: [/^gotveren/, /^gotoğlan/, /^gotlek/] },
        { id: 'tr.ibne', tier: 1, accuse: [/^ibne/] },
        { id: 'tr.pic', tier: 1, accuse: [/^pic$/, /^picler/, /^picin(?!e)/] },
        { id: 'tr.kaltak', tier: 1, accuse: [/^kaltak/] },
        { id: 'tr.pezevenk', tier: 1, accuse: [/^pezevenk/] },
        { id: 'tr.yavsak', tier: 1, accuse: [/^yavşak/] },
        { id: 'tr.got', tier: 2, accuse: [/^got$/, /^gotu$/, /^gotum$/, /^gotune/, /^gotunden/] },
        { id: 'tr.serefsiz', tier: 2, accuse: [/^şerefsiz/, /^puşt/] },
        { id: 'tr.gerizekali', tier: 2, accuse: [/^gerizekal/, /^dangalak/] },
        { id: 'tr.salak', tier: 2, accuse: [/^salak/, /^aptal/] },
        { id: 'tr.lanet', tier: 3, accuse: [/^lanet/, /^kahretsin/] }
        ],
        excuses: [
        // götürmek "to carry" folds to gotur- and starts like göt/got, but none
        // of the tr.got/tr.gotveren accuse patterns actually reach past "gotu"
        // without an anchor, so this is a defensive belt-and-braces entry.
        /^gotur/,
        // şeref "honor" (positive word) is a strict prefix of şerefsiz but the
        // accuse pattern requires the "-siz" tail, so bare şeref never matches —
        // listed here only so the next edit doesn't have to re-derive that.
        /^şeref$/, /^şerefli/,
        /^sikke/, /^sikorsk/
        ],
        fuzzy: []
        };

        var LEXICONS = { ru: RU, en: EN, es: ES, pt: PT, de: DE, fr: FR, id: ID, ko: KO, ar: AR, uk: UK, tr: TR };

        /* A pack's version is a hash of its own patterns — no number to bump by
           hand. The page stamps it on every dictionary signal (lexicon-signals.js),
           and profanity:lexicon-harvest counts only signals from the newest
           version: a miss that an edit already closed stops being a candidate. */
        var versions = {};
        function version(lang) {
            if (versions[lang]) return versions[lang];
            var p = LEXICONS[lang];
            if (!p) return '';
            var src = (p.rules || []).map(function (r) {
                return r.id + r.tier + r.accuse.map(String).join('');
            }).join('|') + '#' + (p.excuses || []).map(String).join('') + '#' + (p.fuzzy || []).map(function (f) { return f.w + f.tier; }).join('');
            var h = 5381;
            for (var i = 0; i < src.length; i++) h = ((h << 5) + h + src.charCodeAt(i)) | 0;
            return (versions[lang] = (h >>> 0).toString(36));
        }

        var api = {
        LEXICONS: LEXICONS,
        supported: function () { return Object.keys(LEXICONS); },
        has: function (lang) { return !!LEXICONS[lang]; },
        get: function (lang) { return LEXICONS[lang] || null; },
        version: version
        };

        if (typeof module !== 'undefined' && module.exports) module.exports = api;
        else root.ProfanityLexicons = api;
})(typeof self !== 'undefined' ? self : this);
